import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { createBranch, deleteBranch, fetchBranches, updateBranch, fetchCities } from "../api/auth";
import { Button, Empty, ErrorNote, PageHeader, PageShell, Panel } from "../components/Page";
import { Select } from "antd";

const MAP_CENTER_BD: [number, number] = [23.6850, 90.3563]; // Bangladesh center
const MAP_ZOOM = 7;

function MapPicker({ latitude, longitude, onSelect, readOnly }: {
  latitude: number | null;
  longitude: number | null;
  onSelect?: (lat: number, lng: number) => void;
  readOnly?: boolean;
}) {
  return (
    <MapContainer
      center={latitude && longitude ? [latitude, longitude] : MAP_CENTER_BD}
      zoom={latitude && longitude ? 15 : MAP_ZOOM}
      style={{ height: "300px", borderRadius: "8px", border: "1px solid var(--color-line)" }}
      className="mb-4"
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {latitude && longitude && <Marker position={[latitude, longitude]} />}
      {!readOnly && onSelect && <MapClickHandler onSelect={onSelect} />}
    </MapContainer>
  );
}

function MapClickHandler({ onSelect }: { onSelect: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export default function BranchesPage() {
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [addressLine, setAddressLine] = useState("");
  const [cityId, setCityId] = useState("");
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: branches, isPending: branchesPending, error } = useQuery({
    queryKey: ["branches"],
    queryFn: fetchBranches,
  });

  const { data: cities, isPending: citiesPending } = useQuery({
    queryKey: ["cities", "BD"],
    queryFn: () => fetchCities("BD"),
  });

  const create = useMutation({
    mutationFn: () =>
      createBranch({
        name: name.trim(),
        ...(addressLine.trim() ? { addressLine: addressLine.trim() } : {}),
        ...(cityId ? { cityId } : {}),
        ...(latitude !== null ? { latitude } : {}),
        ...(longitude !== null ? { longitude } : {}),
      }),
    onSuccess: () => {
      setName("");
      setAddressLine("");
      setCityId("");
      setLatitude(null);
      setLongitude(null);
      setFormError(null);
      void queryClient.invalidateQueries({ queryKey: ["branches"] });
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : "Could not add that showroom"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteBranch(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["branches"] }),
  });

  const togglePublic = useMutation({
    mutationFn: (branch: { id: string; isPublic: boolean }) =>
      updateBranch(branch.id, { isPublic: !branch.isPublic }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["branches"] }),
  });

  if (error)
    return (
      <PageShell>
        <ErrorNote error={error} />
      </PageShell>
    );

  return (
    <PageShell>
      <PageHeader
        eyebrow="Workspace"
        title="Showrooms"
        description="The places your business operates from. Optional, and never shown as a street address on your public page."
      />

      <div className="space-y-4">
        <Panel title="Add a showroom">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
            className="space-y-4"
          >
            <div>
              <label className="label" htmlFor="branch-name">
                Name
              </label>
              <input
                id="branch-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dhanmondi showroom"
                className="field"
              />
            </div>

            <div>
              <label className="label" htmlFor="branch-address">
                Street address
              </label>
              <input
                id="branch-address"
                value={addressLine}
                onChange={(e) => setAddressLine(e.target.value)}
                className="field"
              />
              <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">
                Stored for your records. Your public page shows the showroom name and city only,
                never this address.
              </p>
            </div>

            <div>
              <label className="label" htmlFor="branch-city">
                City
              </label>
              <Select
                id="branch-city"
                value={cityId}
                onChange={(val: string) => {
                  const selected = cities?.find(c => c.id === val);
                  setCityId(val);
                  if (selected?.latitude && selected?.longitude) {
                    setLatitude(selected.latitude);
                    setLongitude(selected.longitude);
                  }
                }}
                disabled={citiesPending}
                className="field"
              >
                {citiesPending ? (
                  <option value="">Loading cities…</option>
                ) : (
                  <>
                    <option value="">Select a city</option>
                    {cities?.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name} ({city.nameBn ?? city.name})
                      </option>
                    ))}
                  </>
                )}
              </Select>
              <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">
                Select the city where this showroom is located.
              </p>
            </div>

            <div>
              <label className="label">
                Exact location (click on map)
              </label>
              <MapPicker
                latitude={latitude}
                longitude={longitude}
                onSelect={(lat, lng) => {
                  setLatitude(lat);
                  setLongitude(lng);
                }}
              />
              {latitude && longitude && (
                <p className="mt-1.5 text-xs text-ink-muted">
                  Coordinates: {latitude.toFixed(6)}, {longitude.toFixed(6)}
                </p>
              )}
              <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">
                Click on the map to set the exact showroom location. This is used for proximity search.
              </p>
            </div>

            <Button type="submit" disabled={create.isPending || name.trim().length === 0}>
              {create.isPending ? "Adding…" : "Add showroom"}
            </Button>
          </form>

          {formError ? (
            <p
              role="alert"
              className="mt-3 rounded-r border-l-[3px] border-danger bg-danger-wash px-3 py-2 text-sm text-danger"
            >
              {formError}
            </p>
          ) : null}
        </Panel>

        <Panel
          title={branches ? `${branches.length} showroom${branches.length === 1 ? "" : "s"}` : "Showrooms"}
          flush
        >
          {branchesPending ? (
            <p className="py-10 text-center text-sm text-ink-muted">Loading…</p>
          ) : !branches || branches.length === 0 ? (
            <Empty title="No showrooms yet">
              Add one if customers need to know where you trade from.
            </Empty>
          ) : (
            <>
              <ul className="divide-y divide-line px-4 sm:hidden">
                {branches.map((branch) => (
                  <li key={branch.id} className="py-3.5">
                    <p className="font-medium text-ink">{branch.name}</p>
                    <p className="mt-0.5 text-sm text-ink-muted">
                      {branch.addressLine ?? "No address"}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-muted">
                      {branch.cityId ? `City: ${branch.cityId}` : "No city set"}
                      {branch.latitude && branch.longitude && ` · Coords: ${branch.latitude.toFixed(4)}, ${branch.longitude.toFixed(4)}`}
                    </p>
                    <div className="mt-2.5 flex gap-2">
                      <Button
                        type="button"
                        variant="quiet"
                        size="sm"
                        disabled={togglePublic.isPending}
                        onClick={() => togglePublic.mutate(branch)}
                      >
                        {branch.isPublic ? "Shown publicly" : "Hidden publicly"}
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(branch.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>

              <table className="hidden w-full text-sm sm:table">
                <caption className="sr-only">Your showrooms</caption>
                <thead>
                  <tr className="border-b border-line bg-paper text-left">
                    <th
                      scope="col"
                      className="px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.02em] text-ink-muted"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.02em] text-ink-muted"
                    >
                      Address
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.02em] text-ink-muted"
                    >
                      City / Coordinates
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.02em] text-ink-muted"
                    >
                      On public page
                    </th>
                    <th scope="col" className="px-4 py-2.5">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {branches.map((branch) => (
                    <tr
                      key={branch.id}
                      className="border-b border-line transition-colors last:border-b-0 hover:bg-primary-wash"
                    >
                      <td className="px-4 py-3 font-medium text-ink">{branch.name}</td>
                      <td className="px-4 py-3 text-ink-muted">{branch.addressLine ?? "—"}</td>
                      <td className="px-4 py-3 text-ink-muted">
                        {branch.cityId ? `City: ${branch.cityId}` : "No city"}
                        {branch.latitude && branch.longitude && (
                          <span className="ml-2 text-xs">({branch.latitude.toFixed(4)}, {branch.longitude.toFixed(4)})</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          type="button"
                          variant="quiet"
                          size="sm"
                          disabled={togglePublic.isPending}
                          onClick={() => togglePublic.mutate(branch)}
                        >
                          {branch.isPublic ? "Shown" : "Hidden"}
                        </Button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(branch.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Panel>
      </div>
    </PageShell>
  );
}